import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

export interface ModalProps {
  open?: boolean;
  visible?: boolean;
  title?: React.ReactNode;
  children?: React.ReactNode;
  onCancel?: (e?: React.MouseEvent) => void;
  onOk?: (e?: React.MouseEvent) => void;
  footer?: React.ReactNode | null;
  width?: number | string;
  destroyOnClose?: boolean;
  confirmLoading?: boolean;
  okText?: string;
  cancelText?: string;
  style?: React.CSSProperties;
  className?: string;
  centered?: boolean;
  maskClosable?: boolean;
  closable?: boolean;
  zIndex?: number;
  [key: string]: any;
}

export interface ModalConfirmConfig {
  title?: React.ReactNode;
  content?: React.ReactNode;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  okText?: string;
  cancelText?: string;
  [key: string]: any;
}

const ModalBase: React.FC<ModalProps> = ({
  open,
  visible,
  title,
  children,
  onCancel,
  onOk,
  footer,
  width = 520,
  destroyOnClose,
  confirmLoading,
  okText = 'OK',
  cancelText = 'Cancel',
  style,
  className,
  centered,
  maskClosable = true,
  closable = true,
  zIndex,
  ...rest
}) => {
  void rest;
  void style;
  void centered;
  const isOpen = open ?? visible ?? false;
  const [everOpened, setEverOpened] = React.useState(isOpen);
  React.useEffect(() => {
    if (isOpen) setEverOpened(true);
  }, [isOpen]);

  const body = destroyOnClose && !isOpen && everOpened ? null : children;

  const defaultFooter = (
    <>
      <MuiButton onClick={onCancel} sx={{ textTransform: 'none' }}>
        {cancelText}
      </MuiButton>
      <MuiButton
        variant="contained"
        onClick={onOk}
        disabled={confirmLoading}
        startIcon={confirmLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        sx={{ textTransform: 'none' }}
      >
        {okText}
      </MuiButton>
    </>
  );

  return (
    <Dialog
      open={isOpen}
      className={className}
      onClose={maskClosable ? onCancel : undefined}
      fullWidth
      maxWidth={false}
      sx={{ zIndex, '& .MuiDialog-paper': { width, maxWidth: width } }}
    >
      {title != null && (
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{title}</span>
          {closable && (
            <IconButton size="small" onClick={onCancel} aria-label="close">
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}
      {(everOpened || !destroyOnClose) && (
        <>
          <DialogContent dividers>{body}</DialogContent>
          {footer !== null && (
            <DialogActions>{footer === undefined ? defaultFooter : footer}</DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
};

function modalConfirm(config: ModalConfirmConfig) {
  const titleText = typeof config.title === 'string' ? config.title : 'Confirm';
  const ok = window.confirm(titleText);
  if (ok) {
    void config.onOk?.();
  } else {
    config.onCancel?.();
  }
}

export type ModalComponent = typeof ModalBase & {
  confirm: typeof modalConfirm;
};

export const Modal = ModalBase as ModalComponent;
Modal.confirm = modalConfirm;

export default Modal;
